import axios, { AxiosResponse } from "axios";
import { Response } from "./apiClient";
import store from "@/core/services/store";
import { AuthUser } from "@/core/_models/authUser.model";
import AuthService from "@/core/services/auth.service";

export default abstract class AxiosClient {
  protected transformOptions(options: any) {
    // add access token to headers if it
    const user = this.getCurrentUser();

    if (user != null) {
      options.headers["Authorization"] = `Bearer ${user.accessToken}`;
    }
    return Promise.resolve(options);
  }

  protected async transformResult(
    url: string,
    response: AxiosResponse,
    processor: (response: AxiosResponse) => any
  ) {
    const path = new URL(url).pathname;
    // check if we need to refresh the jwt
    if (response.status == 401) {
      // just return processor to user store
      if (["/api/applicationuser/login"].includes(path.toLowerCase())) {
        return processor(response);
      }
      // refresh jwt
      const refreshResult = await AuthService.refreshLogin();
      if (refreshResult) {
        const user = this.getCurrentUser();
        if (user) {
          response.config.headers!["Authorization"] = `Bearer ${user.accessToken}`;
          const instance = axios.create();
          // re-run previous request with new jwt
          return processor(await instance.request(response.config));
        }
      }

      store.dispatch("LOGOUT");
      throw new Error("Token refresh failed, logged out.");
    }

    // check if the response is an error response
    // since the response data is just a plain JS object, we have to use duck typing to guess what type it is
    // we want to make sure it is a plain object and not a blob
    if (
      typeof response.data == "object" &&
      Object.prototype.toString.call(response.data) == Object().toString()
    ) {
      const data = response.data as Record<string, any>;

      // if the object contains the properties that the response object does, it's likely a response object
      const isResponse =
        Object.keys(new Response()).every((key) => data.hasOwnProperty(key)) &&
        typeof data.hasError == "boolean" &&
        typeof data.errorMessage == "string";

      const isDotNetProblemDetails =
        ["status", "title", "type"].every((key) => data.hasOwnProperty(key)) &&
        typeof data.status == "number" &&
        ["string", "null", "undefined"].includes(typeof data.detail) &&
        ["string", "null", "undefined"].includes(typeof data.title);

      // check our response type
      if (isResponse) {
        const response = data as Response;
        if (response.hasError) {
          store.commit("appMessages/addErrorMessage", response.errorMessage);
          if ((data.status as number) >= 400) {
            throw new Error(response.errorMessage);
          }
        }
      }

      // check .NET's ProblemDetails or validation result type
      else if (isDotNetProblemDetails) {
        if ((data.status as number) >= 400) {
          const message = (data.detail ?? data.title ?? "An error occurred.") as string;
          store.commit("appMessages/addErrorMessage", message);
          throw new Error(message);
        }
      }
    }

    switch (response.status) {
      case 400: {
        // do not handle 400 responses for these paths
        if (["/api/ApplicationUser/login"].includes(path.toLowerCase())) {
          return processor(response);
        }
        const message = "An error occurred.";
        store.commit("appMessages/addErrorMessage", message);
        throw new Error(message);
      }
      case 401: {
        // do not handle 401 responses for these paths
        if (["/api/ApplicationUser/login"].includes(path.toLowerCase())) {
          return processor(response);
        }
        const message = "Not logged in.";
        store.commit("appMessages/addErrorMessage", message);
        throw new Error(message);
      }
      case 403: {
        // do not handle 403 responses for these paths
        if (["/api/ApplicationUser/login"].includes(path.toLowerCase())) {
          return processor(response);
        }
        const message = "Access to that resource is forbidden.";
        store.commit("appMessages/addErrorMessage", message);
        throw new Error(message);
      }
      case 404: {
        const message = "The requested resource was not found.";
        store.commit("appMessages/addErrorMessage", message);
        throw new Error(message);
      }
      case 500: {
        const message = "There was an error on the server and the request could not be completed.";
        store.commit("appMessages/addErrorMessage", message);
        throw new Error(message);
      }
      default:
        if (response.status < 400) {
          return processor(response);
        } else {
          const message = "An error occurred.";
          store.commit("appMessages/addErrorMessage", message);
          throw new Error(message);
        }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getBaseUrl(_?: string): string {
    return process.env.VUE_APP_API_BASE_URL;
  }

  /**
   * Gets the current User from Vuex store.
   *
   * @returns {(User | null)} The User or null.
   *
   */
  public getCurrentUser(): AuthUser | null {
    return store.getters.currentUser;
  }
}
